import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell, AlertTriangle, Clock, FileText, CheckCircle, Filter,
  CheckCheck, ChevronDown, ClipboardList, Send, XCircle, Info
} from 'lucide-react';
import api from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './Notifications.css';

const TYPE_ICONS = {
  report_submitted:  { icon: Send,           color: 'info' },
  report_approved:   { icon: CheckCircle,    color: 'success' },
  report_rejected:   { icon: XCircle,        color: 'danger' },
  deadline_warning:  { icon: Clock,          color: 'warning' },
  deadline_overdue:  { icon: AlertTriangle,  color: 'danger' },
  document_assigned: { icon: FileText,       color: 'info' },
  task_completed:    { icon: CheckCircle,    color: 'success' },
  plan_not_submitted:{ icon: ClipboardList,  color: 'warning' },
  system:            { icon: Info,           color: 'default' },
};

const PRIORITY_KEYS = ['low', 'normal', 'high', 'critical'];

function Notifications() {
  const { t } = useTranslation(['notifications', 'common']);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ is_read: '', type: '', priority: '' });
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.is_read !== '') params.is_read = filter.is_read;
      if (filter.type) params.type = filter.type;
      if (filter.priority) params.priority = filter.priority;

      const res = await api.getNotifications(params);
      setNotifications(res.data?.results || res.data || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    try {
      await api.markNotificationRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleClick = async (notif) => {
    // Отмечаем прочитанным
    if (!notif.is_read) {
      try {
        await api.markNotificationRead(notif.id);
        setNotifications(prev =>
          prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n)
        );
      } catch { /* ignore */ }
    }
    // Переход к связанному объекту
    if (notif.related_object_type && notif.related_object_id) {
      const routes = {
        report: `/reports/${notif.related_object_id}`,
        document: `/documents/${notif.related_object_id}`,
        plan: `/plans/${notif.related_object_id}`,
      };
      const path = routes[notif.related_object_type];
      if (path) navigate(path);
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('common:timeAgo.justNow');
    if (mins < 60) return t('common:timeAgo.minutesAgo', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('common:timeAgo.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    if (days < 7) return t('common:timeAgo.daysAgo', { count: days });
    return new Date(dateStr).toLocaleDateString(getDateLocale(), {
      day: 'numeric', month: 'short',
    });
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return <div className="notifications-page"><div className="loading-spinner">{t('common:table.loading')}</div></div>;
  }

  return (
    <div className="notifications-page">
      <div className="notif-header">
        <h1><Bell size={24} /> {t('notifications:title')}</h1>
        <span className="notif-total">
          {notifications.length > 0
            ? (unreadCount > 0
              ? t('notifications:totalWithUnread', { total: notifications.length, unread: unreadCount })
              : t('notifications:total', { count: notifications.length }))
            : t('notifications:noNotifications')}
        </span>
        <div className="notif-header-actions">
          <button
            className="notif-btn filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} /> {t('notifications:filters')} <ChevronDown size={14} />
          </button>
          {unreadCount > 0 && (
            <button className="notif-btn mark-all-btn" onClick={handleMarkAllRead}>
              <CheckCheck size={16} /> {t('notifications:markAllRead')}
            </button>
          )}
        </div>
      </div>

      {/* Фильтры */}
      {showFilters && (
        <div className="notif-filters">
          <select
            value={filter.is_read}
            onChange={e => setFilter(prev => ({ ...prev, is_read: e.target.value }))}
          >
            <option value="">{t('notifications:all')}</option>
            <option value="false">{t('notifications:unread')}</option>
            <option value="true">{t('notifications:read')}</option>
          </select>
          <select
            value={filter.type}
            onChange={e => setFilter(prev => ({ ...prev, type: e.target.value }))}
          >
            <option value="">{t('notifications:allTypes')}</option>
            {Object.keys(TYPE_ICONS).map(key => (
              <option key={key} value={key}>{t(`notifications:types.${key}`)}</option>
            ))}
          </select>
          <select
            value={filter.priority}
            onChange={e => setFilter(prev => ({ ...prev, priority: e.target.value }))}
          >
            <option value="">{t('notifications:allPriorities')}</option>
            {PRIORITY_KEYS.map(key => (
              <option key={key} value={key}>{t(`notifications:priorities.${key}`)}</option>
            ))}
          </select>
          <button
            className="notif-btn"
            onClick={() => setFilter({ is_read: '', type: '', priority: '' })}
          >
            {t('notifications:reset')}
          </button>
        </div>
      )}

      {/* Пустое состояние */}
      {notifications.length === 0 && (
        <div className="notif-empty">
          <Bell size={48} />
          <h3>{t('notifications:empty.noNotifications')}</h3>
          <p>
            {filter.is_read !== '' || filter.type || filter.priority
              ? t('notifications:empty.filteredEmpty')
              : t('notifications:empty.allClear')}
          </p>
        </div>
      )}

      {/* Список уведомлений */}
      <div className="notif-list">
        {notifications.map(notif => {
          const iconCfg = TYPE_ICONS[notif.type] || TYPE_ICONS.system;
          const Icon = iconCfg.icon;
          const colorClass = iconCfg.color;
          const typeLabel = t(`notifications:types.${notif.type}`, { defaultValue: notif.type });

          return (
            <div
              key={notif.id}
              className={`notif-card ${colorClass} ${notif.is_read ? 'read' : 'unread'}`}
              onClick={() => handleClick(notif)}
            >
              <div className={`notif-card-icon ${colorClass}`}>
                <Icon size={20} />
              </div>

              <div className="notif-card-content">
                <div className="notif-card-title">{notif.title}</div>
                {notif.message && (
                  <div className="notif-card-message">{notif.message}</div>
                )}
                <div className="notif-card-meta">
                  <span className={`notif-type-badge ${colorClass}`}>{typeLabel}</span>
                  {notif.priority && notif.priority !== 'normal' && (
                    <span className={`notif-priority-badge priority-${notif.priority}`}>
                      {t(`notifications:priorities.${notif.priority}`)}
                    </span>
                  )}
                  <span className="notif-time">{timeAgo(notif.created_at)}</span>
                </div>
              </div>

              {!notif.is_read && (
                <button
                  className="notif-read-btn"
                  onClick={(e) => handleMarkRead(notif.id, e)}
                  title={t('notifications:markRead')}
                >
                  <CheckCircle size={18} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Notifications;
